import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Add auth token from store
        const authStore = JSON.parse(localStorage.getItem('auth-storage') || '{}');
        const token = authStore?.state?.token;

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        // Any status code that lies within the range of 2xx causes this function to trigger
        return response;
    },
    (error) => {
        // Handle errors globally
        const { response } = error;

        if (!response) {
            // Network error
            toast.error('Network error. Please check your connection.');
            return Promise.reject(error);
        }

        // Handle specific status codes
        switch (response.status) {
            case 401:
                // Unauthorized - redirect to login if not on login page.
                // A wrong current password on change-password is also a 401; let the page show that error.
                if (!window.location.pathname.includes('/login') && !error.config?.url?.includes('change-password')) {
                    const authStore = JSON.parse(localStorage.getItem('auth-storage') || '{}');
                    if (authStore?.state?.userType === 'admin') {
                        window.location.href = '/admin/login';
                    } else {
                        window.location.href = '/login';
                    }
                    localStorage.removeItem('auth-storage');
                }
                break;

            case 403:
                // Forbidden
                if (response.data?.mustChangePassword) {
                    window.location.href = '/user/change-password';
                } else {
                    toast.error('You do not have permission to perform this action.');
                }
                break;

            case 404:
                // Not found
                toast.error(response.data?.message || 'Resource not found.');
                break;

            case 422:
                // Validation error
                if (response.data?.errors) {
                    response.data.errors.forEach((error) => {
                        toast.error(error);
                    });
                } else {
                    toast.error(response.data?.message || 'Validation error.');
                }
                break;

            case 429:
                // Too many requests
                toast.error('Too many requests. Please try again later.');
                break;

            case 500:
                // Server error
                toast.error('Server error. Please try again later.');
                break;

            default:
                // Other errors
                if (response.data?.message) {
                    toast.error(response.data.message);
                }
        }

        return Promise.reject(error);
    }
);

// API methods
const apiMethods = {
    // Auth endpoints
    auth: {
        login: (data) => api.post('/auth/login', data),
        adminLogin: (data) => api.post('/auth/admin/login', data),
        logout: () => api.post('/auth/logout'),
        adminLogout: () => api.post('/auth/admin/logout'),
        verify: () => api.get('/auth/verify'),
        adminVerify: () => api.get('/auth/admin/verify'),
        changePassword: (data) => api.post('/auth/change-password', data),
        forgotPassword: (data) => api.post('/auth/forgot-password', data),
        resetPassword: (data) => api.post('/auth/reset-password', data),
        setup2FA: () => api.post('/auth/2fa/setup'),
        verify2FA: (data) => api.post('/auth/2fa/verify', data),
        disable2FA: (data) => api.post('/auth/2fa/disable', data),
    },

    // User endpoints
    users: {
        getProfile: () => api.get('/users/profile'),
        updateProfile: (data) => api.put('/users/profile', data),
        uploadProfilePicture: (formData) =>
            api.post('/users/profile-picture', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }),
        deleteProfilePicture: () => api.delete('/users/profile-picture'),
        getCardPreview: () => api.get('/users/card-preview'),
        regenerateCardUuid: () => api.post('/users/regenerate-card-uuid'),
        getLoginHistory: () => api.get('/users/login-history'),
        updateEmail: (data) => api.post('/users/update-email', data),
        getStats: () => api.get('/users/stats'),

        // Phone number endpoints
        getPhones: () => api.get('/users/phone-numbers'),
        addPhone: (data) => api.post('/users/phone-numbers', data),
        updatePhone: (id, data) => api.put(`/users/phone-numbers/${id}`, data),
        deletePhone: (id) => api.delete(`/users/phone-numbers/${id}`),
        reorderPhones: (data) => api.put('/users/phone-numbers/reorder', data),

        // Email address endpoints
        getEmails: () => api.get('/users/email-addresses'),
        addEmail: (data) => api.post('/users/email-addresses', data),
        updateEmail: (id, data) => api.put(`/users/email-addresses/${id}`, data),
        deleteEmail: (id) => api.delete(`/users/email-addresses/${id}`),
        reorderEmails: (data) => api.put('/users/email-addresses/reorder', data),

        // Nonprofit organization endpoints
        getNonprofits: () => api.get('/users/nonprofit-organizations'),
        addNonprofit: (data) => api.post('/users/nonprofit-organizations', data),
        updateNonprofit: (id, data) => api.put(`/users/nonprofit-organizations/${id}`, data),
        deleteNonprofit: (id) => api.delete(`/users/nonprofit-organizations/${id}`),
        reorderNonprofits: (data) => api.put('/users/nonprofit-organizations/reorder', data),

        // Contact exchange ("Connections")
        getConnections: (params) => api.get('/users/connections', { params }),
        markConnection: (id, read) => api.patch(`/users/connections/${id}`, { read }),
        deleteConnection: (id) => api.delete(`/users/connections/${id}`),
        getConnectionVCard: (id) => api.get(`/users/connections/${id}/vcard`, { responseType: 'blob' }),
    },

    // Admin endpoints
    admin: {
        getDashboard: () => api.get('/admin/dashboard'),
        getUsers: (params) => api.get('/admin/users', { params }),
        getUser: (userId) => api.get(`/admin/users/${userId}`),
        createUser: (data) => api.post('/admin/users', data),
        updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
        toggleUserStatus: (userId) => api.patch(`/admin/users/${userId}/toggle-status`),
        resetUserPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
        deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
        exportUsers: () => api.get('/admin/users/export/csv'),
        getAdmins: () => api.get('/admin/admins'),
        createAdmin: (data) => api.post('/admin/admins', data),
        getActivityLog: (params) => api.get('/admin/activity-log', { params }),
        toggle2FA: (userId, enabled) => api.post(`/admin/users/${userId}/2fa`, { enabled }),
        setUserVerified: (userId, isVerified) => api.patch(`/admin/users/${userId}/verified`, { isVerified }),

        // Settings endpoints
        getSettings: () => api.get('/admin/settings'),
        updateSettings: (data) => api.put('/admin/settings', data),
        testEmail: (data) => api.post('/admin/settings/test-email', data),
        backupDatabase: () => api.post('/admin/settings/backup', { responseType: 'blob' }),
        clearAnalytics: () => api.post('/admin/settings/clear-analytics'),
        getSystemInfo: () => api.get('/admin/settings/system-info'),

        // Admin Account Management endpoints
        getProfile: () => api.get('/admin/profile'),
        changePassword: (data) => api.post('/admin/change-password', data),
        setup2FA: () => api.post('/admin/setup-2fa'),
        verify2FA: (data) => api.post('/admin/verify-2fa', data),
        disable2FA: (data) => api.post('/admin/disable-2fa', data),
    },

    // Announcement endpoints
    announcements: {
        // User endpoints
        getActive: () => api.get('/announcements/user/active'),
        markAsSeen: (id) => api.post(`/announcements/user/${id}/seen`),
        getHistory: () => api.get('/announcements/user/history'),

        // Admin endpoints
        getAll: (params) => api.get('/announcements/admin', { params }),
        get: (id) => api.get(`/announcements/admin/${id}`),
        create: (data) => api.post('/announcements/admin', data),
        update: (id, data) => api.put(`/announcements/admin/${id}`, data),
        delete: (id) => api.delete(`/announcements/admin/${id}`),
        sendEmail: (id) => api.post(`/announcements/admin/${id}/send-email`),
    },

    // Card endpoints
    card: {
        getCard: (uuid) => api.get(`/card/${uuid}`),
        getVCard: (uuid) => api.get(`/card/${uuid}/vcard`),
        getQRCode: (uuid, params) => api.get(`/card/${uuid}/qrcode`, { params }),
        getAnalytics: (uuid) => api.get(`/card/${uuid}/analytics`),
        getDetailedAnalytics: (uuid, params) => api.get(`/card/${uuid}/analytics/detailed`, { params }),
        trackClick: (uuid, data) => api.post(`/card/${uuid}/track-click`, data),
        shortenUrl: (uuid) => api.post(`/card/${uuid}/shorten`),
    },

    // Upload endpoints
    upload: {
        uploadImage: (formData, config) =>
            api.post('/upload/image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                ...config,
            }),
        uploadOrgLogo: (formData) =>
            api.post('/upload/org-logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }),
        deleteFile: (filename) => api.delete(`/upload/file/${filename}`),
        getStats: () => api.get('/upload/stats'),
        cleanup: () => api.post('/upload/cleanup'),
        optimizeImage: (formData) =>
            api.post('/upload/optimize', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                responseType: 'blob',
            }),
    },
};

// Export both the axios instance and methods
export default api;
export { apiMethods };